import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart3, Download } from 'lucide-react';
import { format, subDays } from 'date-fns';
import { downloadCsv, rowsToCsv } from '@/lib/csvExport';

const PLACEMENTS = ['', 'right_sidebar', 'left_sidebar', 'homepage_banner', 'agencies_inline_mobile'];

export function BannerPerformanceReport() {
  const [from, setFrom] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [to, setTo] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [placement, setPlacement] = useState('');

  const fromIso = new Date(from + 'T00:00:00').toISOString();
  const toIso = new Date(to + 'T23:59:59').toISOString();

  const { data: perBanner, isLoading } = useQuery({
    queryKey: ['banner-perf', fromIso, toIso, placement],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('banner_performance_report' as any, {
        _from: fromIso, _to: toIso, _placement: placement || null,
      });
      if (error) throw error;
      return (data as any[]) || [];
    },
  });

  const { data: perPlacement } = useQuery({
    queryKey: ['banner-placement-perf', fromIso, toIso],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('banner_placement_report' as any, {
        _from: fromIso, _to: toIso,
      });
      if (error) throw error;
      return (data as any[]) || [];
    },
  });

  const { data: perCity } = useQuery({
    queryKey: ['banner-city-perf', fromIso, toIso, placement],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('banner_city_report' as any, {
        _from: fromIso, _to: toIso, _placement: placement || null,
      });
      if (error) throw error;
      return (data as any[]) || [];
    },
  });

  const totals = (perBanner || []).reduce(
    (a, r: any) => ({
      imps: a.imps + Number(r.impressions),
      clicks: a.clicks + Number(r.clicks),
      rev: a.rev + Number(r.revenue_etb),
    }),
    { imps: 0, clicks: 0, rev: 0 },
  );
  const totalCtr = totals.imps > 0 ? ((totals.clicks / totals.imps) * 100).toFixed(2) : '0.00';

  const exportCsv = () => {
    if (!perBanner?.length) return;
    downloadCsv(`banner-performance-${from}_to_${to}.csv`, rowsToCsv(perBanner));
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><BarChart3 className="h-5 w-5 text-primary" /> Banner Performance Report</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label>From</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-[160px]" />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-[160px]" />
            </div>
            <div>
              <Label>Placement</Label>
              <Select value={placement || 'all'} onValueChange={(v) => setPlacement(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All placements</SelectItem>
                  {PLACEMENTS.filter(Boolean).map((p) => <SelectItem key={p} value={p}>{p.replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="ml-auto">
              <Button variant="outline" onClick={exportCsv} disabled={!perBanner?.length}>
                <Download className="h-4 w-4 mr-2" /> Export CSV
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Impressions" value={totals.imps.toLocaleString()} />
            <Stat label="Clicks" value={totals.clicks.toLocaleString()} />
            <Stat label="CTR" value={`${totalCtr}%`} />
            <Stat label="Revenue (ETB)" value={totals.rev.toLocaleString()} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">By Placement</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow><TableHead>Placement</TableHead><TableHead>Banners</TableHead><TableHead>Impressions</TableHead><TableHead>Clicks</TableHead><TableHead>CTR</TableHead></TableRow></TableHeader>
            <TableBody>
              {(perPlacement || []).map((r: any) => (
                <TableRow key={r.placement}>
                  <TableCell className="font-medium">{(r.placement || '—').replace(/_/g, ' ')}</TableCell>
                  <TableCell>{r.banners}</TableCell>
                  <TableCell>{Number(r.impressions).toLocaleString()}</TableCell>
                  <TableCell>{Number(r.clicks).toLocaleString()}</TableCell>
                  <TableCell><Badge variant="secondary">{Number(r.ctr).toFixed(2)}%</Badge></TableCell>
                </TableRow>
              ))}
              {!perPlacement?.length && (
                <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No data in selected range</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">By City × Placement</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>City</TableHead>
                <TableHead>Placement</TableHead>
                <TableHead>Banners</TableHead>
                <TableHead>Impressions</TableHead>
                <TableHead>Clicks</TableHead>
                <TableHead>CTR</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(perCity || []).map((r: any, i: number) => (
                <TableRow key={`${r.city}-${r.placement}-${i}`}>
                  <TableCell className="font-medium">{r.city}</TableCell>
                  <TableCell className="text-xs capitalize">{(r.placement || '—').replace(/_/g, ' ')}</TableCell>
                  <TableCell>{r.campaigns}</TableCell>
                  <TableCell>{Number(r.impressions).toLocaleString()}</TableCell>
                  <TableCell>{Number(r.clicks).toLocaleString()}</TableCell>
                  <TableCell><Badge variant="secondary">{Number(r.ctr).toFixed(2)}%</Badge></TableCell>
                </TableRow>
              ))}
              {!perCity?.length && (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No city data captured yet — events must include city info.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Per Banner</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? <Skeleton className="h-40" /> : (
            <Table>
              <TableHeader><TableRow><TableHead>Banner</TableHead><TableHead>Placement</TableHead><TableHead>Advertiser</TableHead><TableHead>Imps</TableHead><TableHead>Clicks</TableHead><TableHead>CTR</TableHead><TableHead>Revenue</TableHead></TableRow></TableHeader>
              <TableBody>
                {(perBanner || []).map((r: any) => (
                  <TableRow key={r.campaign_id}>
                    <TableCell className="max-w-[220px] truncate font-medium">{r.title}</TableCell>
                    <TableCell className="text-xs">{(r.placement || '—').replace(/_/g, ' ')}</TableCell>
                    <TableCell className="text-xs">{r.advertiser_name || '—'}</TableCell>
                    <TableCell>{Number(r.impressions).toLocaleString()}</TableCell>
                    <TableCell>{Number(r.clicks).toLocaleString()}</TableCell>
                    <TableCell><Badge variant="secondary">{Number(r.ctr).toFixed(2)}%</Badge></TableCell>
                    <TableCell>ETB {Number(r.revenue_etb).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
                {!perBanner?.length && (
                  <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No banners in selected range</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="border rounded-lg p-3 bg-card">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-xl font-semibold mt-1">{value}</div>
    </div>
  );
}
